import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollText, RefreshCw, Loader2, ShieldCheck, Search, Download, FileText, BarChart3 } from "lucide-react";

function statusColor(s) {
  if (s >= 500) return "bg-[#FF3B30]/10 text-[#FF3B30]";
  if (s >= 400) return "bg-[#f5a623]/10 text-[#f5a623]";
  return "bg-[#00F593]/10 text-[#00F593]";
}

function SessionAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/session-analytics").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return null;
  const max = Math.max(1, ...data.sessions.map((s) => s.total));
  return (
    <div className="border border-[#222] bg-[#0F0F0F] p-4 mb-4" data-testid="session-analytics">
      <div className="flex items-center gap-2 mb-3"><BarChart3 className="h-4 w-4 text-[#1C8CFF]" />
        <span className="font-heading font-semibold text-sm">Pèfòmans pa Sesyon (Gold signals)</span>
        <span className="font-mono-term text-[10px] text-[#666] ml-auto">Win rate global: {data.totals.win_rate}% · {data.totals.total} signal</span></div>
      <div className="grid grid-cols-4 gap-3">
        {data.sessions.map((s) => (
          <div key={s.session} className="text-center" data-testid={`session-${s.session}`}>
            <div className="h-24 flex items-end justify-center gap-1 mb-2">
              <div className="w-3 rounded-t bg-[#22e07b]" style={{ height: `${Math.max(4, (s.wins / max) * 96)}px` }} title={`${s.wins} wins`} />
              <div className="w-3 rounded-t bg-[#ff5b5b]" style={{ height: `${Math.max(4, (s.losses / max) * 96)}px` }} title={`${s.losses} losses`} />
            </div>
            <div className="font-heading font-semibold text-xs">{s.session}</div>
            <div className="font-mono-term text-[10px] text-[#888]">{s.win_rate}% · {s.total}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-3 justify-center">
        <span className="flex items-center gap-1.5 text-[10px] font-mono-term text-[#888]"><span className="h-2 w-2 rounded-sm bg-[#22e07b]" /> Wins</span>
        <span className="flex items-center gap-1.5 text-[10px] font-mono-term text-[#888]"><span className="h-2 w-2 rounded-sm bg-[#ff5b5b]" /> Losses</span>
      </div>
    </div>
  );
}

export function StaffAuditTab() {
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [exp, setExp] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/staff-audit", { params: { limit: 300 } });
      setLog(data || []);
    } catch { /* ignore */ }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const doExport = async (kind) => {
    setExp(kind);
    try {
      const url = kind === "pdf" ? "/admin/staff-audit/export-pdf" : "/admin/staff-audit/export";
      const { data } = await api.get(url, { responseType: "blob" });
      const mime = kind === "pdf" ? "application/pdf" : "text/csv";
      const blob = URL.createObjectURL(new Blob([data], { type: mime }));
      const a = document.createElement("a");
      a.href = blob; a.download = `staff_action_log.${kind === "pdf" ? "pdf" : "csv"}`; a.click();
      URL.revokeObjectURL(blob);
    } catch { toast.error("Erè ekspòtasyon"); }
    finally { setExp(null); }
  };

  const filtered = q
    ? log.filter((r) =>
        [r.name, r.email, r.action, r.path, r.title].filter(Boolean)
          .some((v) => v.toLowerCase().includes(q.toLowerCase())))
    : log;

  return (
    <div className="space-y-4" data-testid="staff-audit-tab">
      <SessionAnalytics />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-[#1C8CFF]" />
          <span className="font-heading font-semibold">Jounal Aksyon Ekip la</span>
          <span className="font-mono-term text-[10px] text-[#555]">({filtered.length})</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-[#555] absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="staff-audit-search"
              placeholder="Chèche non / aksyon" className="h-8 pl-8 w-52 bg-[#0A0A0A] border-[#222] rounded-sm text-xs" />
          </div>
          <Button size="sm" onClick={() => doExport("csv")} disabled={exp === "csv"} data-testid="staff-audit-export-csv"
            className="h-8 rounded-sm bg-[#1C8CFF] text-white hover:bg-[#0f7ae6] text-xs">
            {exp === "csv" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Download className="h-3.5 w-3.5 mr-1" /> CSV</>}
          </Button>
          <Button size="sm" onClick={() => doExport("pdf")} disabled={exp === "pdf"} data-testid="staff-audit-export-pdf"
            className="h-8 rounded-sm bg-[#FF3B30] text-white hover:bg-[#e02d23] text-xs">
            {exp === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><FileText className="h-3.5 w-3.5 mr-1" /> PDF</>}
          </Button>
          <Button size="sm" onClick={load} disabled={busy} data-testid="staff-audit-refresh"
            className="h-8 rounded-sm bg-white text-black hover:bg-[#e5e5e5] text-xs">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>
      <p className="font-mono-term text-[11px] text-[#666]">
        Tout sa chak manm ekip la fè nan panèl admin lan (kreye manm, bay/retire aksè, chanje wòl, konfigirasyon,
        elatriye). Kenbe pou 90 jou epi efase otomatikman apre.
      </p>

      <div className="border border-[#222] bg-[#0F0F0F] max-h-[560px] overflow-y-auto" data-testid="staff-audit-list">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-[#888] text-sm">Pa gen aksyon anrejistre pou kounye a.</div>
        ) : (
          filtered.map((r, i) => (
            <div key={r.id || i} data-testid={`staff-audit-row-${i}`}
              className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#161616] last:border-0">
              <div className="flex items-center gap-3 min-w-0">
                <ShieldCheck className="h-4 w-4 text-[#555] shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-semibold text-sm truncate">{r.name || r.email || "—"}</span>
                    <span className="text-[10px] font-mono-term uppercase text-[#1C8CFF]">{r.title}</span>
                    <span className={`font-mono-term text-[10px] uppercase px-1.5 py-0.5 rounded-sm ${statusColor(r.status)}`}>{r.status}</span>
                  </div>
                  <div className="font-mono-term text-[11px] text-[#8fb4e6] truncate">{r.action}</div>
                  <div className="font-mono-term text-[10px] text-[#555] truncate">{r.method} {r.path}</div>
                </div>
              </div>
              <span className="font-mono-term text-[11px] text-[#555] shrink-0">{(r.at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
